import { readItemField } from '../compile/item-field-reader';
import { readLayerListEntryUuid } from '../compile/inventory-raw-readers';
import { calculateAverageVitalityFromDice, calculateMaximumVitalityFromDice, getVitalityDieFromCreatureSize } from '../compile/creature-vitality';
import type { Row, SpawnReaders, CreatureSpawnRequest } from './row-plan';

/** Definition-only vitality: never pass a placed creature's saved vitality as the source. */
export function definitionVitalityMaximum(source: Row, read: Pick<SpawnReaders, 'value' | 'die'>,
    method: CreatureSpawnRequest['vitality'] = 'average'): number {
    if (readItemField(source, 'item_type') === 'lair') return 0;
    const multiplier = Number(read.value(source, 'v_dice_multiplier') ?? 0);
    const explicit = read.value(source, 'v_max');
    // A dice-changing template replaces the single creature's fixed definition maximum.
    // Saved/typed instance maxima are handled by the caller, outside this definition read.
    if ((!(multiplier > 0) || multiplier === 1) && explicit !== null && explicit !== undefined && explicit !== '') return Number(explicit);
    const size = readLayerListEntryUuid(read.value(source, 'size')), body = Math.trunc(Number(read.value(source, 'body') ?? 0));
    const baseDice = Math.trunc(Number(read.value(source, 'v_dice') ?? 0));
    const dice = multiplier > 0 ? Math.round(baseDice * multiplier) : baseDice;
    const bonus = Number(read.value(source, 'v_mod') ?? 0);
    const maximum = method === 'max' ? calculateMaximumVitalityFromDice(size, dice, body, bonus)
        : method === 'random' ? Array.from({ length: Math.max(0, dice) }, () => read.die(getVitalityDieFromCreatureSize(size)))
            .reduce((a, b) => a + b, 0) + dice * body + bonus
            : calculateAverageVitalityFromDice(size, dice, body, bonus);
    return Math.max(1, maximum);
}

/** Preserve typed fields; scale only untouched current vitality against the final maximum. */
export function templateChangeVitality(maximum: number, posted: Row, painted: Row): { v_max: number; v_current: number } {
    const oldMax = Number(painted.v_max ?? 0), oldCurrent = Number(painted.v_current ?? 0);
    const v_max = Number(posted.v_max) !== oldMax ? Number(posted.v_max) : maximum;
    const proportional = oldCurrent > 0 ? Math.max(1, Math.floor(v_max * (oldMax > 0 ? oldCurrent / oldMax : 1))) : 0;
    return { v_max, v_current: Number(posted.v_current) !== oldCurrent ? Number(posted.v_current) : Math.min(v_max, proportional) };
}
